import React, { useState } from 'react';
import {
  IAuditoriaDespesaAnaliticaRelevanteResultado,
  IItemDespesaAnaliticaRelevante,
} from '../interfaces/IAuditoriaDespesaAnaliticaRelevante';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaDespesaAnaliticaRelevanteResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaDespesaAnaliticaRelevanteDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const lancamentosDespesaRelevante = resultado.lancamentosDespesaRelevante || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Despesas Analíticas Relevantes (RFB 3.01/3.02) — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          <div className='auditoria-kpi-help'>Sem multa aplicável — aviso de materialidade/mapeamento (CPC 26).</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Lançamentos Analisados / Relevantes</div>
          <div className='auditoria-kpi-value'>
            {resultado.totalLancamentosAnalisados} / <span className='auditoria-contador auditoria-contador-aviso'>{lancamentosDespesaRelevante.length}</span>
          </div>
          <div className='auditoria-kpi-help'>Lançamentos em conta analítica de despesa (RFB 3.01/3.02) acima de R$ 100.000,00.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Bases contábil e fiscal aplicadas às despesas analíticas relevantes.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div className='mb-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Despesas Analíticas Relevantes (RFB 3.01/3.02)</h3>
        <p className='text-muted'>
          Lançamentos na conta analítica (a que efetivamente recebe o lançamento) mapeada para o Plano Referencial
          da RFB nos grupos 3.01 (Despesas Operacionais/Financeiras) ou 3.02 (Provisão IRPJ/CSLL), com valor superior
          ao limite de materialidade de R$ 100.000,00.
        </p>

        {lancamentosDespesaRelevante.length === 0 && (
          <div className='text-muted mt-4'>Nenhum lançamento em despesa analítica relevante identificado no período.</div>
        )}

        {lancamentosDespesaRelevante.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta Analítica</th>
                  <th>Referencial RFB</th>
                  <th>Data</th>
                  <th>Histórico</th>
                  <th className='text-end'>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {lancamentosDespesaRelevante.map((item: IItemDespesaAnaliticaRelevante, idx) => {
                  const chave = `dr-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo status-aviso' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                        </td>
                        <td>
                          <div>{item.contaReferencial}</div>
                          <div className='text-muted' style={{ fontSize: 11 }}>{item.descricaoReferencial}</div>
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td style={{ maxWidth: 220 }}>{item.historico}</td>
                        <td className='text-end'>{formatCurrency(item.valor)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div className='auditoria-alert status-AVISO mb-2'>
                                <i className='bi bi-info-circle-fill auditoria-alert-icon' />
                                <div>{item.mensagemObrigatoria}</div>
                              </div>
                              <div><strong>Lançamento (Histórico):</strong> {item.historico}.</div>
                              <div><strong>Mapeamento Referencial:</strong> {item.contaReferencial} — {item.descricaoReferencial}</div>
                              <div className='mt-1 text-muted' style={{ fontSize: 12 }}>{item.memoriaCalculo}</div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AuditoriaDespesaAnaliticaRelevanteDetalhe;
